import { Component } from '@theme/component';
import { ProductSelectEvent } from '@shopify/events';
import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  pendingRequestUrl;

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {boolean} */
  #updating = false;

  /**
   * Resolves when the in-flight variant fetch settles (success, error, or abort).
   * @type {Promise<unknown>}
   */
  updateComplete = Promise.resolve();

  /** @returns {boolean} Whether a variant section fetch is currently in flight. */
  get isUpdating() {
    return this.#updating;
  }

  connectedCallback() {
    super.connectedCallback();

    this.boundVariantChanged = this.variantChanged.bind(this);
    this.addEventListener('change', this.boundVariantChanged);

    const target = this.closest('.shopify-section');
    if (target) {
      target.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.boundVariantChanged);
    const target = this.closest('.shopify-section');
    if (target) {
      target.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }

    // Abort any pending fetch
    this.#fetchController?.abort();
    this.#fetchController = null;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
    event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

  if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Check if we're on product page (not in quick add/view dialogs)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('quick-add-dialog')
      && !event.target.closest('quick-view-dialog');

    // For combined listings: if variant belongs to different product, morph entire page
    // This is needed because product change affects recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, null, loadsNewProduct), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @returns {string} The request URL.
   */
  /**
   * Builds request URL for fetching updated variant section
   * Handles combined listings where products may have different URLs
   * @param {HTMLElement} selectedOption - The selected variant option
   * @param {string | null} [source] - Optional source parameter
   * @param {boolean} [fullPage] - Request the whole product page instead of a single section.
   *   A section_id response carries only that section's markup, so morphing <main> needs the full page.
   * @returns {string} The request URL with section_id and option_values
   */
  buildRequestUrl(selectedOption, source = null, fullPage = false) {
    // Support combined listings: use connected product URL if variant belongs to different product
    // pendingRequestUrl tracks the last product URL for rapid variant changes
    let productUrl = selectedOption.dataset.connectedProductUrl || this.pendingRequestUrl || this.dataset.productUrl;
    this.pendingRequestUrl = productUrl;
    const params = [];

    // Include selected option values in request (unless source is provided)
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    }

    if (this.getSectionId() && !fullPage) {
      // Remove existing query params before adding new ones
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${this.getSectionId()}&${params.join('&')}`;
    }

    if (productUrl?.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Announces the shopper's pick on Shopify's standard event, ahead of the fetch the way the
   * runtime itself times these, so apps and shopping agents can follow which variant is being
   * considered. The theme keeps driving its own UI off VariantUpdateEvent — nothing in the theme
   * listens for this. Subclasses that fetch on their own settle the returned promise themselves.
   *
   * @returns {{ promise: Promise<any>, resolve: (value: any) => void, reject: (reason?: any) => void }}
   */
  announceProductSelect() {
    const selection = ProductSelectEvent.createPromise();

    this.dispatchEvent(
      new ProductSelectEvent({
        productId: this.dataset.productId ?? '',
        promise: selection.promise,
      })
    );

    return selection;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;
    this.#updating = true;

    const selection = this.announceProductSelect();

    this.updateComplete = fetch(requestUrl, { signal: controller.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = undefined;
        let html = new DOMParser().parseFromString(responseText, 'text/html');

        // The overlay group puts a quick-action template on every page, so unwrapping it
        // unconditionally would throw away the document <main> is in.
        if (!shouldMorphMain) {
          const template = html.querySelector('template.quick-action-template');
          if (template) {
            html = template.content.cloneNode(true);
          }
        }
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          selection.reject(new Error('Variant data missing from the section response'));
          return;
        }

        const variant = JSON.parse(textContent);
        selection.resolve({ variantId: String(variant?.id ?? '') });

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          if (this.selectedOptionId) {
            const newProduct = this.resolveNewProduct(html);

            this.dispatchEvent(
              new VariantUpdateEvent(variant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        selection.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (this.#fetchController === controller) {
          this.#updating = false;
        }
      });
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const blockId = this.dataset.blockId;
    const newVariantPicker = blockId
      ? event.detail.data.html.querySelector(`variant-picker[data-block-id="${blockId}"]`)
      : null;

    if (!newVariantPicker) {
      return;
    }

    morph(this, newVariantPicker);
  }

  /**
   * Reports the product a response belongs to, when that is not the product this picker was
   * rendered for. Only dialogs reach this: a product page morphs the whole of <main> instead.
   * Every block keyed on data-product-id has to be re-pointed at the same moment this picker is,
   * or the next variant change finds the two out of step and skips its own update.
   * @param {Document | DocumentFragment} html - The re-rendered section.
   * @returns {{ id: string, url: string } | null} The new product, or null when it has not changed.
   */
  resolveNewProduct(html) {
    const blockId = this.dataset.blockId;
    const source = blockId
      ? html.querySelector(`variant-picker[data-block-id="${blockId}"]`)
      : html.querySelector('variant-picker');

    if (!(source instanceof HTMLElement)) return null;

    const { productId, productUrl } = source.dataset;
    if (!productId || !productUrl) return null;

    const changed = this.dataset.productId !== productId;

    this.dataset.productId = productId;
    this.dataset.productUrl = productUrl;

    return changed ? { id: productId, url: productUrl } : null;
  }

  /**
 * Re-renders the entire main content.
 * @param {Document} newHtml - The new HTML.
 */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain, { ...MORPH_OPTIONS, reinitComponents: true });
  }


  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected][data-option-value-id], .variant-option--buttons input[data-option-value-id]:checked, .product-card__swatches input[data-option-value-id]:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId || undefined;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll(
      'select option[selected][data-option-value-id], .variant-option--buttons input[data-option-value-id]:checked'
    ));

    return selectedOptions.map((option) => option.dataset.optionValueId).filter(Boolean);
  }

  getSectionId() {
    return this.getAttribute('data-section-id');
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
